// src/components/exercise/CompletionScreen.tsx
import React, { useState } from 'react';
import { useExerciseStore } from '../../stores/useExerciseStore';
import ProgressRing from './ProgressRing';

const C = {
  surface: '#0d1220',
  card:    'rgba(255,255,255,0.03)',
  border:  'rgba(255,255,255,0.06)',
  accent:  '#4f8cf7',
  accent2: '#6366f1',
  success: '#34d399',
  error:   '#f87171',
  warn:    '#fbbf24',
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
};

function getMessage(pct: number): { title: string; sub: string; color: string } {
  if (pct >= 90) return { title: 'Mastery Achieved',   sub: 'Outstanding performance. You have a strong command of this section.',     color: C.success };
  if (pct >= 70) return { title: 'Good Job',           sub: 'Great effort. Review the questions you missed to improve further.',        color: C.accent  };
  return         { title: 'Keep Practicing',           sub: "Don't give up — practice makes perfect. Try the section again.",          color: C.warn    };
}

const CompletionScreen: React.FC = () => {
  const score          = useExerciseStore(s => s.score);
  const totalExercises = useExerciseStore(s => s.totalExercises);
  const answerHistory  = useExerciseStore(s => s.answerHistory);
  const restartSection = useExerciseStore(s => s.restartSection);
  const returnToOverview = useExerciseStore(s => s.returnToOverview);

  const [showReview,    setShowReview]    = useState(false);
  const [retryHover,    setRetryHover]    = useState(false);
  const [continueHover, setContinueHover] = useState(false);
  const [reviewHover,   setReviewHover]   = useState(false);

  const pct      = totalExercises > 0 ? Math.round((score / totalExercises) * 100) : 0;
  const msg      = getMessage(pct);

  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      padding:       '48px 28px 36px',
      gap:           '32px',
      overflowY:     'auto',
      flex:          1,
    }}>
      {/* Score ring */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
        <div style={{
          padding:       '6px',
          borderRadius:  '50%',
          background:    `${msg.color}10`,
          border:        `1px solid ${msg.color}25`,
          boxShadow:     `0 0 40px ${msg.color}20`,
        }}>
          <ProgressRing
            percent={pct}
            size={120}
            stroke={6}
            color={msg.color}
            bgColor="rgba(255,255,255,0.06)"
            fontSize={22}
          />
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize:      'clamp(1.3rem, 3vw, 1.6rem)',
            fontWeight:    800,
            color:         msg.color,
            letterSpacing: '-0.01em',
            marginBottom:  '8px',
          }}>
            {msg.title}
          </div>
          <div style={{
            fontSize:   '0.88rem',
            color:      C.muted,
            maxWidth:   '300px',
            lineHeight: 1.6,
          }}>
            {msg.sub}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{
        display:      'flex',
        background:   'rgba(255,255,255,0.03)',
        borderRadius: '16px',
        border:       '1px solid rgba(255,255,255,0.06)',
        overflow:     'hidden',
        width:        '100%',
        maxWidth:     '320px',
        boxShadow:    '0 4px 16px rgba(0,0,0,0.2)',
      }}>
        <StatCell label="Score"   value={`${score}/${totalExercises}`} color={C.accent} />
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <StatCell label="Correct" value={`${score}`}                   color={C.success} />
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <StatCell
          label="Wrong"
          value={`${totalExercises - score}`}
          color={totalExercises - score > 0 ? C.error : C.muted}
        />
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '320px' }}>
        <button
          onClick={() => void restartSection()}
          onMouseEnter={() => setRetryHover(true)}
          onMouseLeave={() => setRetryHover(false)}
          style={{
            padding:       '14px',
            borderRadius:  '999px',
            border:        `1px solid ${retryHover ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
            background:    retryHover ? 'rgba(255,255,255,0.06)' : 'transparent',
            color:         C.text,
            fontSize:      '0.82rem',
            fontWeight:    700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor:        'pointer',
            transition:    'all 0.2s ease',
            fontFamily:    'inherit',
          }}
        >
          Practice Again
        </button>
        <button
          onClick={() => void returnToOverview()}
          onMouseEnter={() => setContinueHover(true)}
          onMouseLeave={() => setContinueHover(false)}
          style={{
            padding:       '14px',
            borderRadius:  '999px',
            border:        'none',
            background:    continueHover
              ? '#2fbd88'
              : `linear-gradient(135deg, ${C.success} 0%, #1fa37a 100%)`,
            color:         '#fff',
            fontSize:      '0.82rem',
            fontWeight:    700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor:        'pointer',
            transition:    'all 0.2s ease',
            boxShadow:     continueHover ? '0 8px 24px rgba(52,211,153,0.35)' : '0 4px 16px rgba(52,211,153,0.2)',
            transform:     continueHover ? 'translateY(-2px)' : 'none',
            fontFamily:    'inherit',
          }}
        >
          Continue Learning →
        </button>
      </div>

      {/* Answer review */}
      {answerHistory.length > 0 && (
        <div style={{ width: '100%', maxWidth: '380px' }}>
          <button
            onClick={() => setShowReview(v => !v)}
            onMouseEnter={() => setReviewHover(true)}
            onMouseLeave={() => setReviewHover(false)}
            style={{
              width:         '100%',
              display:       'flex',
              justifyContent:'space-between',
              alignItems:    'center',
              padding:       '12px 18px',
              borderRadius:  '12px',
              border:        '1px solid rgba(255,255,255,0.06)',
              background:    reviewHover ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
              color:         C.muted,
              fontSize:      '0.8rem',
              fontWeight:    700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor:        'pointer',
              transition:    'all 0.15s ease',
              fontFamily:    'inherit',
            }}
          >
            <span>Review Answers ({answerHistory.length})</span>
            <span style={{ transition: 'transform 0.2s ease', transform: showReview ? 'rotate(180deg)' : 'none' }}>▾</span>
          </button>

          {showReview && (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '320px', overflowY: 'auto' }}>
              {answerHistory.map((rec, i) => (
                <div key={i} style={{
                  padding:      '12px 14px',
                  borderRadius: '10px',
                  background:   'rgba(255,255,255,0.03)',
                  border:       `1px solid ${rec.isCorrect ? 'rgba(52,211,153,0.2)' : 'rgba(248,113,113,0.15)'}`,
                  display:      'flex',
                  flexDirection:'column',
                  gap:          '4px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ fontSize: '0.75rem', color: C.muted }}>Q{i + 1}.</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: rec.isCorrect ? C.success : C.error, flexShrink: 0 }}>
                      {rec.isCorrect ? '✓' : '✗'} {rec.scoreEarned}/{rec.maxScore}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: C.text, lineHeight: 1.4 }}>
                    {rec.question.length > 90 ? rec.question.substring(0, 90) + '…' : rec.question}
                  </div>
                  {!rec.isCorrect && (
                    <div style={{ fontSize: '0.76rem', color: C.muted, marginTop: '2px' }}>
                      Your answer: <span style={{ color: C.error }}>{rec.userAnswerDisplay}</span>
                      {'  ·  '}
                      Correct: <span style={{ color: C.success }}>{rec.correctAnswerDisplay}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCell: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 8px', gap: '4px' }}>
    <div style={{ fontSize: '1.2rem', fontWeight: 800, color }}>{value}</div>
    <div style={{ fontSize: '0.65rem', color: '#8b9cc4', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
  </div>
);

export default CompletionScreen;
